import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import OtpInput from "react-otp-input";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";
import loginStyles from "../Login/index.module.css";

function EligibilityVerifyOtp() {
  const navigate = useNavigate();
  const { data } = useParams();

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!data) { navigate("*"); return; }
    try {
      const decoded = JSON.parse(atob(data));
      if (!decoded.applicationId || !decoded.userId || !decoded.mobile) {
        navigate("*");
        return;
      }
      setMobile(decoded.mobile);
      sendOtp(decoded.mobile);
    } catch {
      navigate("*");
    }
  }, [data]);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const formatTimer = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const sendOtp = async (mob: string) => {
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_DASHBOARD_URL}/login/send-otp`, {
        mobile: mob,
        source: "eligibility",
      });
      setOtpSent(true);
      setTimer(54);
    } catch {
      setError("Failed to send OTP. Please try again.");
    }
  };

  const handleResend = () => {
    if (timer > 0) return;
    setOtp("");
    setError("");
    sendOtp(mobile);
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError("Enter 6 digit OTP"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/login/verify-otp`,
        { mobile, otp }
      );
      if (res.status === 200) {
        sessionStorage.setItem("eligibility_auth_token", res?.data?.data);
        sessionStorage.setItem("eligibility_verified", "true");
        navigate(`/eligibility/${data}`, { replace: true });
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* Header */}
              <div className={styles.cardHeader}>
                <div
                  style={{
                    width: "2.4rem",
                    height: "2.4rem",
                    borderRadius: "50%",
                    background: "#F4E5DD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z"
                      fill="#d32028"
                      opacity="0.85"
                    />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Verify Mobile</p>
              </div>

              <div className={styles.content}>
                {/* Mobile Number (read-only) */}
                <span className={loginStyles.fieldLabel} style={{ marginBottom: "0.4rem" }}>Mobile Number</span>
                <div className={loginStyles.phoneRowWrapper}>
                  <div className={loginStyles.phoneRow}>
                    <span className={loginStyles.countryCode}>+91 ▾</span>
                    <input
                      className={loginStyles.phoneInput}
                      type="tel"
                      value={mobile}
                      readOnly
                    />
                  </div>
                </div>

                {/* OTP input */}
                {otpSent && (
                  <div style={{ marginTop: "1.1rem" }}>
                    <p style={{ fontSize: "0.82rem", color: "#666", margin: "0 0 0.75rem", lineHeight: 1.5 }}>
                      Enter the 6-digit code sent to your mobile.
                    </p>
                    <span className={loginStyles.fieldLabel}>Enter OTP</span>
                    <OtpInput
                      value={otp}
                      onChange={(val: string) => { setOtp(val); setError(""); }}
                      numInputs={6}
                      renderSeparator={<span></span>}
                      renderInput={(props: any) => <input {...props} />}
                      inputStyle={{
                        width: "2.6rem",
                        height: "2.8rem",
                        borderRadius: "8px",
                        border: "1px solid #d0d5dd",
                        fontSize: "1.2rem",
                        textAlign: "center",
                        fontFamily: "Outfit, sans-serif",
                        color: "#333",
                        background: "#f9fafb",
                        outline: "none",
                      }}
                      containerStyle={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "0.4rem",
                        margin: "0.5rem 0 0",
                      }}
                    />

                    <div className={loginStyles.resendRow}>
                      <span>
                        Didn't receive the code?{" "}
                        <span
                          className={timer > 0 ? loginStyles.resendLinkDisabled : loginStyles.resendLink}
                          onClick={handleResend}
                        >
                          Resend OTP
                        </span>
                      </span>
                      {timer > 0 && (
                        <span className={loginStyles.timerText}>
                          <span className={loginStyles.timerDot}></span>
                          Request new code in {formatTimer(timer)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {error && <p className={loginStyles.errorText}>{error}</p>}

                <button
                  className={loginStyles.primaryBtn}
                  onClick={handleVerify}
                  disabled={loading || !otpSent}
                >
                  {loading ? "Verifying..." : "Verify & Proceed"}
                </button>
              </div>
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityVerifyOtp;
